import type { HwcgIncidentSpecificsState } from './hwcgIncidentSpecifics';

export const HWCG_EOC_CHANNELS = ['inApp', 'email', 'sms', 'voice'] as const;

export type HwcgEocChannel = (typeof HWCG_EOC_CHANNELS)[number];

export type HwcgEocPoint = {
  type: 'Point';
  coordinates: [number, number];
};

export type HwcgEocNoticeState = {
  locationLabel: string;
  location: HwcgEocPoint | null;
  recipientContactIds: string[];
  /** Null keeps the generated script, so later intake edits still fill it in. */
  confidentialityScript: string | null;
  activateScript: string | null;
  notifyScript: string | null;
  /** Responsible Party activation: when people should arrive. */
  reportToTime: string;
  channels: HwcgEocChannel[];
};

export const HWCG_EOC_CONFIDENTIALITY_SCRIPT =
  'This is a confidential HWCG communication. By continuing, you agree to keep the contents of this notification confidential. If you agree, press 1 on your phone, or reply 1 by text.';

type EocScriptSpecifics = Pick<
  HwcgIncidentSpecificsState,
  'time' | 'date' | 'companyName' | 'wellName' | 'blockNumber' | 'waterDepthFt'
>;

function filled(value: string | null | undefined, fallback: string): string {
  const trimmed = value?.trim() ?? '';
  return trimmed || fallback;
}

function depthPhrase(value: string | null | undefined): string {
  const trimmed = value?.trim() ?? '';
  if (!trimmed) return 'an unknown water depth';
  return /ft\b/i.test(trimmed) ? trimmed : `${trimmed} ft`;
}

export function hwcgEocActivateScript(specifics: EocScriptSpecifics): string {
  const when = `${filled(specifics.time, 'an unknown time')} on ${filled(specifics.date, 'an unknown date')}`;
  return `This is an HWCG Activation Notice. At ${when}, ${filled(specifics.companyName, 'a responsible party')} reported an incident at ${filled(specifics.wellName, 'an unnamed well')} and ${filled(specifics.blockNumber, 'an unnamed block')}, water depth ${depthPhrase(specifics.waterDepthFt)}. Please respond 1 when the EOC is activated. Respond 9 if the EOC cannot be activated.`;
}

export function hwcgResponsiblePartyActivateScript(
  specifics: EocScriptSpecifics,
  reportToLocation: string,
  reportToTime: string
): string {
  const when = `${filled(specifics.time, 'an unknown time')} on ${filled(specifics.date, 'an unknown date')}`;
  const place =
    reportToLocation.trim() ||
    filled(specifics.companyName, 'the responsible party office');
  const arrive =
    reportToTime.trim() || filled(specifics.time, 'an unknown time');
  return `This is an HWCG Activation Notice. At ${when}, ${filled(specifics.companyName, 'a responsible party')} reported an incident at ${filled(specifics.wellName, 'an unnamed well')} and ${filled(specifics.blockNumber, 'an unnamed block')}, water depth ${depthPhrase(specifics.waterDepthFt)}. Please report to ${place} at ${arrive}. Please respond 1 when you have reported. Respond 9 if you cannot report.`;
}

export function hwcgEocNotifyScript(
  specifics: Pick<HwcgIncidentSpecificsState, 'time' | 'date'>
): string {
  const when = `${filled(specifics.time, 'an unknown time')} on ${filled(specifics.date, 'an unknown date')}`;
  return `This is an HWCG Activation Notice. At ${when}, a HWCG member reported an incident. No action is required at this time. Please respond with 1 to acknowledge receipt of this message.`;
}

export function createEmptyHwcgEocNotice(): HwcgEocNoticeState {
  return {
    locationLabel: '',
    location: null,
    recipientContactIds: [],
    confidentialityScript: null,
    activateScript: null,
    notifyScript: null,
    reportToTime: '',
    // Include SMS by default: the confidentiality script asks recipients to reply 1 by text.
    channels: ['inApp', 'email', 'sms', 'voice'],
  };
}

export function hwcgActivateScriptFor(
  groupId: 'eoc' | 'responsible_party',
  notice: HwcgEocNoticeState,
  specifics: EocScriptSpecifics
): string {
  if (notice.activateScript) return notice.activateScript;
  if (groupId === 'responsible_party') {
    return hwcgResponsiblePartyActivateScript(
      specifics,
      notice.locationLabel,
      notice.reportToTime
    );
  }
  return hwcgEocActivateScript(specifics);
}

export function hwcgEocCompanyMatches(
  memberCompany: string | null | undefined,
  responsibleParty: string
): boolean {
  const company = responsibleParty.trim().toLowerCase();
  if (!company) return false;
  return (memberCompany ?? '').trim().toLowerCase() === company;
}

export function hwcgGroupNoticePayload(
  groupId: 'eoc' | 'responsible_party' | 'mutual_aid' | 'service_provider',
  action: 'activate' | 'notify',
  notice: HwcgEocNoticeState,
  specifics: EocScriptSpecifics &
    Pick<HwcgIncidentSpecificsState, 'time' | 'date'>
) {
  const activateGroup =
    groupId === 'responsible_party' ? 'responsible_party' : 'eoc';
  return {
    action,
    recipientContactIds: notice.recipientContactIds,
    confidentialityScript:
      notice.confidentialityScript ?? HWCG_EOC_CONFIDENTIALITY_SCRIPT,
    noticeScript:
      action === 'activate'
        ? hwcgActivateScriptFor(activateGroup, notice, specifics)
        : (notice.notifyScript ?? hwcgEocNotifyScript(specifics)),
    channels: notice.channels,
  };
}

export function hwcgEocCreatePayload(
  action: 'activate' | 'notify',
  notice: HwcgEocNoticeState,
  specifics: EocScriptSpecifics &
    Pick<HwcgIncidentSpecificsState, 'time' | 'date'>
) {
  return {
    action,
    recipientContactIds: notice.recipientContactIds,
    confidentialityScript:
      notice.confidentialityScript ?? HWCG_EOC_CONFIDENTIALITY_SCRIPT,
    noticeScript:
      action === 'activate'
        ? hwcgActivateScriptFor('eoc', notice, specifics)
        : (notice.notifyScript ?? hwcgEocNotifyScript(specifics)),
    channels: notice.channels,
  };
}

export function hwcgEocRecipients<
  T extends { id: string; memberCompany?: string | null },
>(
  contacts: T[],
  responsibleParty: string,
  selectedIds?: readonly string[]
): T[] {
  const selected = selectedIds ? new Set(selectedIds) : null;
  const seen = new Set<string>();
  const matches: T[] = [];
  for (const contact of contacts) {
    if (seen.has(contact.id)) continue;
    if (!hwcgEocCompanyMatches(contact.memberCompany, responsibleParty))
      continue;
    if (selected && !selected.has(contact.id)) continue;
    seen.add(contact.id);
    matches.push(contact);
  }
  return matches;
}
